import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { getCurrencySymbol } from 'utils/currencyUtils';
import withTranslate from '../../containers/widgets/withTranslate';
import {
    ALERT_LEVELS,
    ARR_CURRENCY,
    DATA_BADGE_MAP,
    DATA_TEST_ID_AD_SPEND_CAP,
    DATA_TEST_ID_CAP,
    DATA_TEST_ID_CURRENCY,
    DATA_TEST_ID_END_DATE,
    DATA_TEST_ID_ORG_PLAN_DETAILS,
    DATA_TEST_ID_START_DATE,
    DATA_TEST_ID_TIMEZONE,
    HARSH_ALERT_LEVELS,
    PACING_TYPES,
} from '../consts';
import { calculateColorByContractPercent, contractPercent, getOrgPlanDetailsBadgeText } from '../utils';
import { getAdminModeEnabled, getIsAlertPaused, getIsPayingCustomer, getOrganizationTier } from '../../selectors/user';
import css from './OrgPlanDetails.css';
import { Button, Label } from '../../components/widgets';
import CircularProgressBar from '../../components/molecules/CircularProgressBar';
import PlanDetailsButton from './PlanDetailsButton';
import { CUSTOMER_TIERS } from '../../utils/consts';
import Card from './Card';
import pacingCardCss from './PacingCard.css';
import CapsList from './CapsList';
import UsageAlert from './UsageAlert';
import AccountPlanAPI from '../service';
import { getImpersonationFromQuery } from '../../utils/url';
import toaster from '../../utils/toaster';

const OrgPlanDetails = ({
    type,
    timezone,
    currency,
    totalConversionsCap = 0,
    adSpendCap = 0,
    contractStartDate,
    contractEndDate,
    translate,
    planPrice = 0,
    pacingTextWrapper = () => '',
    upgradePlansRef = null,
    alerts = [],
    caps = {},
    selfServeConversionsRollover = 0,
}) => {
    const adminMode = useSelector(state => getAdminModeEnabled(state));
    const tier = useSelector(getOrganizationTier) || '';
    const isPaying = useSelector(getIsPayingCustomer);
    const percent = contractPercent(contractStartDate, contractEndDate);
    const planTimeColor = calculateColorByContractPercent(percent, contractEndDate);
    const isFreeTier = tier === CUSTOMER_TIERS.FREE;
    const isPaused = useSelector(getIsAlertPaused);
    // Check for alerts
    const hasAlerts = alerts?.length > 0;
    const hasHarshAlert = alerts?.some(alert => HARSH_ALERT_LEVELS.includes(alert.alertLevel));

    const hasEventsCap = !isFreeTier && !!caps.event;
    const [isAuditing, setIsAuditing] = useState(false);

    const handleAuditEvents = async () => {
        // Guard against rapid repeat clicks while the (heavy) trigger request is
        // in flight — the audit opens in a new tab, so we only disable for the
        // duration of the request and re-enable once the tab is opened.
        if (isAuditing) {
            return;
        }
        setIsAuditing(true);
        // Open the tab synchronously to claim the click's user-gesture. Opening it
        // after the await would be blocked by popup blockers (window.open then
        // returns null without throwing) — same pattern the Reports download uses.
        const auditWindow = window.open('about:blank', '_blank');
        try {
            const response = await new AccountPlanAPI().auditEvents();
            const validationId = response?.value?.validationId;
            if (!validationId) {
                auditWindow?.close();
                return;
            }
            let url = `/logs/download?validation_id=${validationId}&validation_type=events`;
            const impersonatedOrganization = getImpersonationFromQuery('customer_id');
            if (impersonatedOrganization) {
                url += `&customer_id=${impersonatedOrganization}`;
            }
            if (auditWindow) {
                auditWindow.location.href = url;
            } else {
                window.location.assign(url);
            }
        } catch (err) {
            auditWindow?.close();
            console.error('Audit Events API call failed:', err);
            toaster.error(translate('STATIC.PAGES.ACCOUNT_PLAN.ERRORS.AUDIT_EVENTS_FAILED'));
        } finally {
            setIsAuditing(false);
        }
    };

    const renderContent = () => (
        <>
            <div className={css.mainInfo}>
                <div className={css.capColumn}>
                    {type === PACING_TYPES.CONVERSIONS ? (
                        <div className={css.labelWrapper} data-testid={DATA_TEST_ID_CAP}>
                            <Label
                                className={css.labelTitle}
                                text={`${
                                    totalConversionsCap === Infinity
                                        ? translate('STATIC.PAGES.ACCOUNT_PLAN.UNLIMITED')
                                        : totalConversionsCap.toLocaleString()
                                } ${translate(`STATIC.PAGES.ACCOUNT_PLAN.${isPaying ? 'PAID' : 'FREE'}_INSTALLS`)}`}
                            />
                            {isPaying ? (
                                <Label
                                    type="p"
                                    text={pacingTextWrapper('STATIC.PAGES.ACCOUNT_PLAN.INSTALL_CAP')}
                                    tooltip={
                                        selfServeConversionsRollover > 0
                                            ? translate('STATIC.PAGES.ACCOUNT_PLAN.TOOLTIPS.SELF_SERVE_ROLLOVER', {
                                                  rollover: selfServeConversionsRollover.toLocaleString(),
                                              })
                                            : 'STATIC.PAGES.ACCOUNT_PLAN.TOOLTIPS.PACING_CARD'
                                    }
                                />
                            ) : (
                                <>
                                    <Label
                                        type="p"
                                        text={pacingTextWrapper('STATIC.PAGES.ACCOUNT_PLAN.INSTALL_CAP_FREE')}
                                    />
                                    <Label
                                        type="p"
                                        text="STATIC.PAGES.ACCOUNT_PLAN.INSTALL_CAP_FREE_INFO"
                                        tooltip="STATIC.PAGES.ACCOUNT_PLAN.TOOLTIPS.PACING_CARD"
                                    />
                                </>
                            )}
                        </div>
                    ) : (
                        <div className={css.labelWrapper} data-testid={DATA_TEST_ID_AD_SPEND_CAP}>
                            <Label
                                className={css.labelTitle}
                                text={
                                    adSpendCap === Infinity
                                        ? 'STATIC.PAGES.ACCOUNT_PLAN.UNLIMITED'
                                        : `${getCurrencySymbol(
                                              ARR_CURRENCY
                                          )}${adSpendCap.toLocaleString()} ${ARR_CURRENCY}`
                                }
                            />
                            <Label type="p" text={pacingTextWrapper('STATIC.PAGES.ACCOUNT_PLAN.AD_SPEND_CAP')} />
                        </div>
                    )}
                    {!isFreeTier && <CapsList alerts={alerts} caps={caps} defaultExpanded={hasAlerts} isPaused={isPaused} onAuditEvents={hasEventsCap ? handleAuditEvents : undefined} isAuditing={isAuditing} />}
                </div>
                <div className={css.dateInfoWrapper}>
                    <CircularProgressBar
                        progressPercentage={percent}
                        strokeColor={planTimeColor}
                        iconName="calendar-week-filled"
                    />
                    <div className={css.dateInfo}>
                        <div className={css.labelWrapper} data-testid={DATA_TEST_ID_END_DATE}>
                            <Label
                                className={classNames(css.labelTitle, css.endDate)}
                                text={contractEndDate}
                                style={{
                                    '--end-date-color':
                                        planTimeColor === 'var(--attention)' ? 'var(--attention)' : 'var(--gray700)',
                                }}
                            />
                            <Label type="p" text="STATIC.PAGES.ACCOUNT_PLAN.EXPIRATION_DATE" />
                        </div>
                        <div className={css.labelWrapper} data-testid={DATA_TEST_ID_START_DATE}>
                            <Label className={css.labelTitle} text={contractStartDate} />
                            <Label
                                type="p"
                                text="STATIC.PAGES.ACCOUNT_PLAN.START_DATE"
                                tooltip="STATIC.PAGES.ACCOUNT_PLAN.TOOLTIPS.PLAN_DETAILS_START_DATE"
                            />
                        </div>
                    </div>
                </div>
                <div className={css.multiLabelWrapper}>
                    <div className={css.labelWrapper} data-testid={DATA_TEST_ID_CURRENCY}>
                        <Label className={css.labelTitle} text={currency} />
                        <Label type="p" text="STATIC.PAGES.ACCOUNT_PLAN.CURRENCY" />
                    </div>
                    <div className={css.labelWrapper} data-testid={DATA_TEST_ID_TIMEZONE}>
                        <Label className={css.labelTitle} text={timezone} />
                        <Label type="p" text="STATIC.PAGES.ACCOUNT_PLAN.TIMEZONE" />
                    </div>
                </div>
                {adminMode && (
                    <div className={css.labelWrapper}>
                        <Label
                            className={css.labelTitle}
                            text={`${getCurrencySymbol(ARR_CURRENCY)} ${planPrice.toLocaleString()} ${ARR_CURRENCY}`}
                        />
                        <Label type="p" text="STATIC.PAGES.ACCOUNT_PLAN.PLAN_PRICE" />
                    </div>
                )}
            </div>
            {!isFreeTier && hasAlerts && !isPaused && <UsageAlert alerts={alerts} translate={translate} />}
        </>
    );

    return (
        <Card
            dataTestId={DATA_TEST_ID_ORG_PLAN_DETAILS}
            contentClassName={css.content}
            cardClassName={classNames({
                [pacingCardCss.attention]: hasHarshAlert && !isPaused,
            })}
            header={
                <>
                    <div className={css.headerMain}>
                        <div className={css.title}>
                            <Translate id="STATIC.PAGES.ACCOUNT_PLAN.PLAN_DETAILS" />
                        </div>
                        <div
                            className={classNames(css.tierBadge, css[tier.toLowerCase()])}
                            data-testid={DATA_BADGE_MAP[tier]}
                        >
                            <Translate id={getOrgPlanDetailsBadgeText(tier)} />
                        </div>
                    </div>
                    {tier === CUSTOMER_TIERS.FREE ? (
                        <Button
                            type="secondary"
                            text="STATIC.PAGES.ACCOUNT_PLAN.BUTTONS.BUY_CONVERSIONS"
                            onClick={() => upgradePlansRef?.current?.scrollIntoView({ behavior: 'smooth' })}
                        />
                    ) : (
                        <PlanDetailsButton />
                    )}
                </>
            }
        >
            {renderContent()}
        </Card>
    );
};

OrgPlanDetails.propTypes = {
    type: PropTypes.oneOf(Object.values(PACING_TYPES)).isRequired,
    timezone: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
    contractStartDate: PropTypes.string.isRequired,
    contractEndDate: PropTypes.string.isRequired,
    translate: PropTypes.func.isRequired,
    planPrice: PropTypes.number,
    totalConversionsCap: PropTypes.number,
    adSpendCap: PropTypes.number,
    pacingTextWrapper: PropTypes.func,
    upgradePlansRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
    alerts: PropTypes.arrayOf(
        PropTypes.shape({
            metric: PropTypes.string.isRequired,
            alertLevel: PropTypes.number.isRequired,
            usagePercent: PropTypes.number.isRequired,
            cap: PropTypes.number.isRequired,
            runRatePercent: PropTypes.number,
        })
    ),
    caps: PropTypes.objectOf(PropTypes.number),
    selfServeConversionsRollover: PropTypes.number,
};

export default withTranslate(OrgPlanDetails);
